import HubRestClient from '../services/hubClient/HubRestClient';
import getEndPoint, { API_ENDPOINTS } from '../services/hubClient/apiConstants';


import AuthManager from '../services/AuthManager';
import config from '../services/config';
import Storage from '../services/storage';
import { emailRegex } from '../services/validations';

import { getHubAction, getPayloadWithAllowedQueryParams } from './utils';

import * as AuthActions from '../constants/authTypes';
import { INVALID_PASSWORD_MESSAGE, ACCOUNT_BLOCKED_MESSAGE } from '../constants/strings';


function formatErrors(requestBody, msgKey) {
  if (requestBody && requestBody.messages) {
    const { messages } = requestBody;
    const { errors } = requestBody;
    const value = messages[msgKey];
    if (value && !Array.isArray(value)) {
      return [value];
    }
    if (errors.identity && errors.identity.includes('already_verified') && !emailRegex(requestBody.additional_data.identity.value)) {
      return errors.identity;
    }
    return value || Object.values(messages);
  } else if (requestBody && requestBody[msgKey]) {
    return [requestBody[msgKey]];
  }
  else if (requestBody && requestBody.errors && requestBody.errors[msgKey]) {
    return requestBody.errors[msgKey]
  }
  return ['Some error occurred. Please try again later'];
}

export async function fetchUser(dispatch, params) {
  dispatch({ type: getHubAction(AuthActions.FETCH_USER_INITIATED) });
  try {
    const payload = getPayloadWithAllowedQueryParams(params);
    await fetchHubApiToken(payload);
    const response = await HubRestClient.findOrCreate(payload);
    dispatch({ type: getHubAction(AuthActions.FETCH_USER_SUCCESS), payload: response });
    return response;
  } catch (e) {
    const body = await e.response;
    const errorMsgs = formatErrors(body);
    dispatch({ type: getHubAction(AuthActions.FETCH_USER_FAILURE), payload: errorMsgs });
  }
}


export async function resetFetchUser(dispatch) {
  dispatch({ type: getHubAction(AuthActions.FETCH_USER_RESET) });
}

export async function createUser(dispatch, params) {
  dispatch({ type: getHubAction(AuthActions.CREATE_USER_INITIATED) });
  try {
    const payload = getPayloadWithAllowedQueryParams(params);
    const response = await HubRestClient.findOrCreate(payload);
    dispatch({ type: getHubAction(AuthActions.CREATE_USER_SUCCESS), payload: response.data });
    return response
  } catch (e) {
    const body = await e.response;
    const errorMsgs = formatErrors(body);
    dispatch({ type: getHubAction(AuthActions.CREATE_USER_FAILURE), payload: errorMsgs });
  }
}

export async function brandNameCheck(brandName) {
  try {
    const response = await RestClient.post('/api/v1/validations/brand_name', {
      brand_name: brandName,
    });
    return response
  } catch (e) {
    return {
      is_allowed: true,
    }
  }
}

export async function verifyOtp(dispatch, params) {
  dispatch({ type: getHubAction(AuthActions.VERIFY_OTP_INITIATED) });
  try {
    let response = await HubRestClient.verifyOtp(params);
    response = response || { fetching: false, success: true };
    dispatch({ type: getHubAction(AuthActions.VERIFY_OTP_SUCCESS), payload: response });
    return response;
  } catch (e) {
    const body = await e.response;
    const erroMsgs = formatErrors(body);
    dispatch({ type: getHubAction(AuthActions.VERIFY_OTP_FAILURE), payload: erroMsgs });
    throw new Error(erroMsgs);
  }
}

export async function verifyEmailonlyOtp(dispatch, params) {
  dispatch({ type: getHubAction(AuthActions.VERIFY_OTP_INITIATED) });
  try {
    let response = await HubRestClient.verifyEmailOnlyOtp(params);
    response = response || { fetching: false, success: true };
    dispatch({ type: getHubAction(AuthActions.VERIFY_EMAIL_OTP_SUCCESS), payload: response });
    return response;
  } catch (e) {
    const body = await e.response;
    const erroMsgs = formatErrors(body);
    dispatch({ type: getHubAction(AuthActions.VERIFY_OTP_FAILURE), payload: erroMsgs });
    throw new Error(erroMsgs);
  }
}

export async function loginWithPassword(dispatch, params) {
  dispatch({ type: getHubAction(AuthActions.LOGIN_WITH_PASSWORD_INITIATED) });
  try {
    const response = await HubRestClient.login(params);
    dispatch({
      type: getHubAction(AuthActions.LOGIN_WITH_PASSWORD_SUCCESS),
      payload: { ...response, email: params.email }
    });
    AuthManager.persistFullToken(response);
    return response;
  } catch (e) {
    const body = await e.response;
    let erroMsgs;
    if (body.error === 'locked_user') {
      erroMsgs = [ACCOUNT_BLOCKED_MESSAGE];
    } else if (body.error === 'invalid_grant') {
      erroMsgs = [INVALID_PASSWORD_MESSAGE];
    } else {
      erroMsgs = formatErrors(body);
    }
    dispatch({ type: getHubAction(AuthActions.LOGIN_WITH_PASSWORD_FAILURE), payload: erroMsgs });
    throw new Error(erroMsgs);
  }
}

export async function setPassword(dispatch, params) {
  dispatch({ type: getHubAction(AuthActions.SET_PASSWORD_INITIATED) });
  try {
    const response = await HubRestClient.setPassword(params);
    dispatch({ type: AuthActions.SET_PASSWORD_SUCCESS, payload: response.data });
    return response;
  } catch (e) {
    const body = await e.response;
    const erroMsgs = formatErrors(body, 'cause');
    dispatch({ type: getHubAction(AuthActions.SET_PASSWORD_FAILURE), payload: erroMsgs });
    throw new Error(erroMsgs);
  }
}

export async function sendOtp(dispatch, params) {
  dispatch({ type: getHubAction(AuthActions.SEND_OTP_INITIATED) });
  try {
    const response = await HubRestClient.sendOtp(params);
    dispatch({ type: getHubAction(AuthActions.SEND_OTP_SUCCESS), payload: response.data });
    return response;
  } catch (e) {
    const body = await e.response;
    const erroMsgs = formatErrors(body);
    dispatch({ type: getHubAction(AuthActions.SEND_OTP_FAILURE), payload: erroMsgs });
  }
}

export async function sendVerificationLink(dispatch, params) {
  dispatch({ type: getHubAction(AuthActions.SEND_VERIFY_LINK_INITIATED) });
  try {
    const response = await HubRestClient.sendVerifyLink(params);
    dispatch({
      type: getHubAction(AuthActions.SEND_VERIFY_LINK_SUCCESS),
      payload: response.data
    });
    return response;
  } catch (e) {
    const body = await e.response;
    const errorMsgs = formatErrors(body);
    dispatch({
      type: getHubAction(AuthActions.SEND_VERIFY_LINK_FAILURE),
      payload: errorMsgs
    });
  }
}

export function resetVerifyOtp() {
  return { type: getHubAction(AuthActions.VERIFY_OTP_RESET) };
}

export async function fetchOrCreatePlatformUser(dispatch, params, userId) {
  dispatch({ type: getHubAction(AuthActions.FETCH_OR_CREATE_PLATFORM_USER_INTIATED) });
  try {
    const queryString = AuthManager.getQueryParams();
    const url = `${config.get('createUserUrl', '/create_user')}/${userId}${queryString}`;
    const response = await HubRestClient.post(url, params);
    AuthManager.persistUserToLocalStorage(response);
    dispatch({ type: getHubAction(AuthActions.FETCH_OR_CREATE_PLATFORM_USER_SUCCESS), payload: response });
    return response;
  } catch (e) {
    const body = await e.response;
    const erroMsgs = formatErrors(body, (body && body.errors && body.errors.base) ? 'base' : 'error');
    dispatch({ type: getHubAction(AuthActions.FETCH_OR_CREATE_PLATFORM_USER_FAILURE), payload: erroMsgs });
    throw { message: erroMsgs, errors: erroMsgs };
  }
}

export async function updateUserProfile(dispatch, params) {
  try {
    const response = await HubRestClient.put(getEndPoint('UPDATE_USER_PROFILE'), params);
    return response;
  } catch (e) {
    const body = await e.response;
    const erroMsgs = formatErrors(body);
    throw { message: erroMsgs };
  }
}

export async function fetchHubApiToken() {
  if (!AuthManager.getHubApiToken()) {
    await AuthManager.fetchHubApiToken();
  }
  const expireIn = Storage.getExpiryInSeconds('hubToken');
  if (expireIn) {
    setTimeout(() => {
      AuthManager.removeHubApiToken();
      fetchHubApiToken();
    }, expireIn * 1000);
  }
}

export async function forgotPasswordSendLink(dispatch, params, token) {
  dispatch({ type: getHubAction(AuthActions.SEND_RESET_LINK_INITIATED) });
  try {
    const response = await HubRestClient.sendResetLink(params, token);
    dispatch({
      type: getHubAction(AuthActions.SEND_RESET_LINK_SUCCESS),
      payload: response.data
    });
    return response;
  } catch (e) {
    const body = await e.response;
    const errorMsgs = formatErrors(body);
    dispatch({
      type: getHubAction(AuthActions.SEND_RESET_LINK_FAILURE),
      payload: errorMsgs
    });
    return { error: body?.data?.messages?.user || 'Some error occurred. Please try again later' }
  }
}

export function clearUser(dispatch) {
  dispatch({ type: getHubAction(AuthActions.CLEAR_USER) });
}

export async function fetchResellerData(dispatch) {
  dispatch({type: getHubAction(AuthActions.FETCH_RESELLER_INITITATED)});
  try {
    const queryString = AuthManager.getQueryParams();
    const reseller_uuid = new URLSearchParams(queryString).get('reseller_id');
    const url = `${process.env.PARTNER_HOST}${API_ENDPOINTS.FETCH_PARTNER_DETAILS}?reseller_uuid=${reseller_uuid}`;
    await fetchHubApiToken();
    const response = await HubRestClient.get(url);
    dispatch({ type: getHubAction(AuthActions.FETCH_RESELLER_SUCCESS), payload: response });
    return response;
  } catch (e) {
    const body = await e.response;
    const erroMsgs = formatErrors(body, 'error');
    dispatch({ type: getHubAction(AuthActions.FETCH_RESELLER_FAILURE), payload: erroMsgs });
    throw { message: erroMsgs, errors: erroMsgs };
  }
}